<template>
  <div class="hub-order-pane" :style="height ? { height: height + 'px' } : null">
    <div v-if="problem" class="hub-order-pane-info">
      <p class="text-muted">{{ problemText }}</p>
      <button v-if="canRetry" type="button" class="btn btn-default" v-on:click="prepare">{{ _('Try again') }}</button>
    </div>
    <HubOrderConversation v-else-if="orderId && ready" theme="pos" :organisation-id="'shop:' + shopId" :order-id="orderId" />
    <p v-else class="text-muted hub-order-pane-info">{{ _('Loading...') }}</p>
  </div>
</template>

<script>
import { useMessaging } from '#hub-messaging/composables/useMessaging'
import { setLocale } from '#hub-messaging/lib/i18n'
import { hubProblemText, prepareHubOrder, startHubMessaging } from '~/lib/hub'

// An order's conversation with its customer in the order view: Johku Hub's HubOrderConversation
// (from the #hub-messaging layer) in the POS's look. The order's details go to Hub first, so the
// conversation reads without Johku and the customer's email links to the order page.
export default {
  props: {
    shopId: { type: String, required: true },
    // { identifier, locale, orderUrl } of the order
    order: { type: Object, required: true }
  },
  data () {
    return {
      messaging: useMessaging(),
      orderId: null,
      problem: '',
      // Fitted to the space between the pane's top and the order's save bar
      height: 0
    }
  },
  computed: {
    ready () {
      return this.messaging.status === 'ready'
    },
    problemText () {
      return hubProblemText(this.problem, this._)
    },
    canRetry () {
      return this.problem === 'HUB_UNAVAILABLE' || this.problem === 'HUB_REFUSED'
    }
  },
  watch: {
    // Another order; not every change to this one
    'order.identifier' () {
      this.prepare()
    }
  },
  created () {
    setLocale(this.$store.state.locale)
    this.prepare()
  },
  mounted () {
    this.fit()
    window.addEventListener('resize', this.fit)
    // What's above the tab changes after this mounts (the order loads, a lock notice appears, the
    // title's buttons wrap). The order view scrolls inside a box of fixed height, so its own size
    // doesn't change: watch the parts inside it, and parts coming and going.
    const details = this.$el.closest('.order-details')
    if (details && typeof ResizeObserver !== 'undefined') {
      this.resizes = new ResizeObserver(() => this.fit())
      const observeChildren = () => {
        this.resizes.disconnect()
        for (const child of details.children) {
          this.resizes.observe(child)
        }
      }
      observeChildren()
      this.mutations = new MutationObserver(() => {
        observeChildren()
        this.fit()
      })
      this.mutations.observe(details, { childList: true })
    }
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.fit)
    if (this.resizes) {
      this.resizes.disconnect()
    }
    if (this.mutations) {
      this.mutations.disconnect()
    }
  },
  methods: {
    // The conversation fills the order view down to its save bar, so the composer sits just above it
    fit () {
      const el = this.$el
      if (!el || !el.getBoundingClientRect) {
        return
      }
      const top = el.getBoundingClientRect().top
      const column = el.closest('.order-details')
      const bar = column && column.parentElement ? column.parentElement.querySelector(':scope > .save-bar') : null
      const barTop = bar ? bar.getBoundingClientRect().top : 0
      const bottom = barTop > top ? barTop : window.innerHeight
      const height = Math.max(320, Math.floor(bottom - top - 10))
      if (Math.abs(height - this.height) > 1) {
        this.height = height
      }
    },
    async prepare () {
      this.problem = ''
      this.orderId = null
      try {
        const [result] = await Promise.all([prepareHubOrder(this.shopId, this.order), startHubMessaging(this.shopId)])
        this.orderId = result.orderId
      } catch (error) {
        this.problem = error.code || 'HUB_UNAVAILABLE'
      }
    }
  }
}
</script>

<style scoped>
.hub-order-pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.hub-order-pane > * {
  flex: 1;
  min-height: 0;
}

.hub-order-pane-info {
  padding: 15px 0;
}
</style>
